import React from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { uxEvent } from '../../../../flux/actions/ux'
import {
  selectProductMediaType,
  selectProductMedia,
  selectProductPreviewImage,
} from '../../../../flux/selectors/product'
import MediaImage from '../../Generics/Media/MediaImage'
import MediaVideo from '../../Generics/Media/MediaVideo'
import LoadingSkeleton from '../../Generics/LoadingSkeleton'
import productHasValidVideo from '../../../../helpers/productHasValidVideo'
import { selectCurrentTheme } from '../../../../flux/selectors/ux'

const StyledMedia = styled.div(() => ({
  display: 'flex',
  justifyContent: 'center',
}))

const ProductMedia = ({
  productId,
  isAutoPlayEnabled,
  onVideoStart,
  onVideoStep,
  onVideoEnterFullscreen,
  className,
}) => {
  const mediaType = useSelector(selectProductMediaType(productId))
  const src = useSelector(selectProductMedia(productId))
  const previewSrc = useSelector(selectProductPreviewImage(productId))
  const hasVideo = productHasValidVideo(mediaType)
  const theme = useSelector(selectCurrentTheme)

  if (!mediaType) {
    return (
      <StyledMedia>
        <LoadingSkeleton height="35vh" margin="10px 0" />
      </StyledMedia>
    )
  }

  return (
    <StyledMedia data-test="ProductMedia" className={className} type={mediaType}>
      {
        hasVideo
          ? (
            <MediaVideo
              lazy={false}
              src={src}
              previewSrc={previewSrc}
              isAutoPlayEnabled={isAutoPlayEnabled}
              onVideoStart={onVideoStart}
              onVideoStep={onVideoStep}
              onVideoEnterFullscreen={onVideoEnterFullscreen}
              type={mediaType}
              theme={theme}
            />
          )
          : (
            <MediaImage
              lazy={false}
              src={src}
            />
          )
        }
    </StyledMedia>
  )
}

ProductMedia.defaultProps = {
  isAutoPlayEnabled: false,
  className: '',
}

ProductMedia.propTypes = {
  productId: PropTypes.string.isRequired,
  isAutoPlayEnabled: PropTypes.bool,
  onVideoStart: PropTypes.func.isRequired,
  onVideoStep: PropTypes.func.isRequired,
  onVideoEnterFullscreen: PropTypes.func.isRequired,
  className: PropTypes.string,
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  onVideoEnterFullscreen: () => {
    dispatch(uxEvent({
      name: 'click',
      target: 'ProductDetail/EnterVideoFullscreen',
      params: { productId: ownProps.productId },
    }))
  },
  onVideoStep: ({ step, seeked }) => {
    dispatch(uxEvent({
      name: 'step',
      target: 'ProductDetail/VideoStep',
      params: { productId: ownProps.productId, step, seeked },
    }))
  },
  onVideoStart: () => {
    dispatch(uxEvent({
      name: 'click',
      target: 'ProductDetail/VideoStart',
      params: { productId: ownProps.productId },
    }))
  },
})

ProductMedia.displayName = 'ProductMedia'
ProductMedia.whyDidYouRender = true

export default connect(null, mapDispatchToProps)(
  React.memo(ProductMedia),
)
